import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Breadcrumb, Button, Checkbox, Col, Divider, Form, Input, Modal, Radio, Row, Select, Space } from "antd";
import { MenuOutlined, WarningOutlined } from "@ant-design/icons";
import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";
import { AlertType, useAlert } from "../provider/AlertProvider";
import { Link } from "react-router-dom";
import { CheckboxValueType } from "antd/es/checkbox/Group";

import * as Request from "../commons/request";
import * as Types from "../commons/typeDTO";
import * as String from "../commons/string";
import * as Common from "../commons/common";
import * as Utils from "../utils/utils";

import DriverLicenseInfo from "../components/DriverLicenseInfo";
import MyCoupon from "../components/MyCoupon";
import MyHistory from "../components/MyHistory";
import CryptoJS from "crypto-js";

type AccountStatusType = {
    blocked: boolean;
    accountLocked: boolean;
    accountExpired: boolean;
    withdrawn: boolean;
};

function UserDetail() {
    const param = useParams();
    const alert = useAlert();
    const [form] = Form.useForm();

    const [user, setUser] = useState<Types.UserDto>();
    const [inputType, setInputType] = useState<boolean>();
    const [resetPassword, setResetPassword] = useState<boolean>();
    const [checkCorporate, setCheckCorporate] = useState<boolean>();
    const [openCheckbox, setOpenCheckbox] = useState<boolean>();
    const [confirmModalShow, setConfirmModalShow] = useState<boolean>(false);
    const [accountStatus, setAccountStatus] = useState<AccountStatusType>({
        blocked: false,
        accountLocked: false,
        accountExpired: false,
        withdrawn: false,
    });
    const [email, setEmail] = useState<string>();
    const [b2bs, setB2bs] = useState<Array<Types.B2bDto>>();
    const newPassword = "R" + user?.phone + "!";

    useEffect(() => {
        requestGetB2bs();
    }, []);

    useEffect(() => {
        if (param.id1) {
            const email = CryptoJS.AES.decrypt(param.id1, "userEmail").toString(CryptoJS.enc.Utf8);
            requestGetUser(email);
            setEmail(email);
        }
    }, [param]);

    useEffect(() => {
        if (user) {
            form.setFieldsValue({
                ...user,
                createdDate: formatDateField(user.createdDate),
                withdrawnDate: formatDateField(user.withdrawnDate),
                lastLoginDate: formatDateField(user.lastLoginDate),
                blockedDate: formatDateField(user.blockedDate),
                promotionCodes: user?.b2bs?.map((b2b) => b2b.promotionCode),
            });
            user.userType === "CORPORATE" ? setCheckCorporate(true) : setCheckCorporate(false);
        } else {
            form.resetFields();
        }

        setUserStatusValue();
    }, [user]);

    //get user
    const requestAxiosGetUser = async (email: string) => {
        const response = await utilAxiosWithAuth().get(Request.USER_URL + "/" + email, {
            params: { email },
        });
        return response.data;
    };

    const { loading: loadingGetUser, error: errorGetUser, data: resultGetUser, execute: requestGetUser } = useAsyncAxios(requestAxiosGetUser);

    useEffect(() => {
        if (!resultGetUser) return;

        console.log("resultGetUser", resultGetUser);
        setUser(resultGetUser.user);
    }, [resultGetUser]);

    useEffect(() => {
        if (!errorGetUser) return;

        console.log("errorGetUser", errorGetUser);
    }, [errorGetUser]);

    //update user
    const requestAxiosUpdateUser = async (user: Types.UserDto) => {
        const response = await utilAxiosWithAuth().put(Request.USER_URL + "/" + email, user);
        return response.data;
    };

    const { loading: loadingUpdateUser, error: errorUpdateUser, data: resultUpdateUser, execute: requestUpdateUser } = useAsyncAxios(requestAxiosUpdateUser);

    useEffect(() => {
        if (resultUpdateUser === null) return;
        console.log("resultUpdateUser", resultUpdateUser);

        requestGetUser(resultUpdateUser.user.email);
        alert.setAlert(AlertType.SUCCESS, "회원정보 수정 성공", "회원정보 수정을 완료하였습니다.");
    }, [resultUpdateUser]);

    useEffect(() => {
        if (errorUpdateUser === null) return;

        console.log("errorUpdateUser", errorUpdateUser);
    }, [errorUpdateUser]);

    //get b2bs
    const requestAxiosGetB2bs = async () => {
        const response = await utilAxiosWithAuth().get(Request.RENT_B2B_LIST_URL, {
            params: { page: 0, size: 0 },
        });
        return response.data;
    };

    const { loading: loadingGetB2bs, error: errorGetB2bs, data: resultGetB2bs, execute: requestGetB2bs } = useAsyncAxios(requestAxiosGetB2bs);

    useEffect(() => {
        if (resultGetB2bs === null) return;

        console.log("resultGetB2bs", resultGetB2bs);
        setB2bs(resultGetB2bs.b2bs.content);
    }, [resultGetB2bs]);

    useEffect(() => {
        if (errorGetB2bs === null) return;

        console.log("errorGetB2bs", errorGetB2bs);
    }, [errorGetB2bs]);

    const formatDateField = (date: string | undefined): string => {
        return date ? Utils.utcToLocalTime(date, Common.FORMAT_SHORT_DATE_TIME_WITHOUT_SECOND) : String.dash;
    };

    const setUserStatusValue = () => {
        if (user?.accountExpired || user?.accountLocked || user?.blocked || user?.withdrawn) {
            setOpenCheckbox(true);
            setAccountStatus({
                blocked: user?.blocked,
                accountLocked: user?.accountLocked,
                accountExpired: user?.accountExpired,
                withdrawn: user?.withdrawn,
            });
        } else {
            setOpenCheckbox(false);
            setAccountStatus({
                blocked: false,
                accountLocked: false,
                accountExpired: false,
                withdrawn: false,
            });
        }
    };

    const onFinish = (value: any) => {
        value["accountExpired"] = accountStatus?.accountExpired;
        value["accountLocked"] = accountStatus?.accountLocked;
        value["blocked"] = accountStatus?.blocked;
        value["withdrawn"] = accountStatus?.withdrawn;

        value["createdDate"] = user?.createdDate;
        value["withdrawnDate"] = user?.withdrawnDate;
        value["lastLoginDate"] = user?.lastLoginDate;
        value["blockedDate"] = user?.blockedDate;
        value["b2bs"] = value?.promotionCodes?.map((code: string) => ({ promotionCode: code }));
        delete value["promotionCodes"];

        requestUpdateUser(value);
        setInputType(false);
    };

    const onChange = (value: CheckboxValueType[]) => {
        setAccountStatus({
            ...accountStatus,
            blocked: value.includes("blocked"),
            accountLocked: value.includes("accountLocked"),
            accountExpired: value.includes("accountExpired"),
            withdrawn: value.includes("withdrawn"),
        });
    };

    return (
        <div className="container">
            <Space size={30} direction="vertical" style={{ width: "100%" }}>
                <div>
                    <Breadcrumb
                        items={[
                            {
                                title: (
                                    <Link to={Common.PAGE_LIST_USERS}>
                                        <MenuOutlined /> 통합회원 관리
                                    </Link>
                                ),
                            },
                            {
                                title: "회원정보 상세보기",
                            },
                        ]}
                    />
                    <h3 className="title">회원정보 상세보기</h3>
                </div>
                <div>
                    <Row justify={"space-between"}>
                        <Col span={6}>
                            <Divider orientation="left">
                                <span>회원정보</span>
                            </Divider>
                        </Col>
                        <Col span={18} style={{ textAlign: "right" }}>
                            {!inputType ? (
                                <Space>
                                    <Button onClick={() => setConfirmModalShow(true)} danger disabled>
                                        <WarningOutlined />
                                        강제 삭제
                                    </Button>
                                    <Button
                                        onClick={(e) => {
                                            setInputType(true);
                                            e.preventDefault();
                                        }}
                                    >
                                        수정
                                    </Button>
                                </Space>
                            ) : (
                                <Space>
                                    <Button
                                        onClick={() => {
                                            setInputType(false);
                                            form.setFieldsValue(user);
                                            setUserStatusValue();
                                        }}
                                    >
                                        취소
                                    </Button>
                                    <Button type="primary" onClick={() => form.submit()}>
                                        완료
                                    </Button>
                                </Space>
                            )}
                        </Col>
                    </Row>
                    <Form layout="inline" form={form} onFinish={onFinish}>
                        <Row className="separate-table" style={{ width: "100%" }}>
                            <Col span={12}>
                                <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                                    <Form.Item className="searchForm" name="userType" label="계정 타입">
                                        <Radio.Group
                                            options={[
                                                { label: "개인 회원", value: "INDIVIDUAL" },
                                                { label: "법인 회원", value: "CORPORATE" },
                                            ]}
                                            disabled={!inputType}
                                            onChange={(e) => {
                                                e.target.value === "CORPORATE" ? setCheckCorporate(true) : setCheckCorporate(false);
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                                    <Form.Item className="searchForm" name="name" label="이름">
                                        <Input disabled placeholder={user?.name || String.dash} />
                                    </Form.Item>
                                </Col>
                                <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                                    <Form.Item
                                        className="searchForm"
                                        name="email"
                                        label="이메일"
                                        rules={[
                                            {
                                                validator: (_, value) => Utils.validateEmail(true, false, value),
                                            },
                                        ]}
                                    >
                                        <Input disabled={!inputType} placeholder={user?.email || String.dash} />
                                    </Form.Item>
                                </Col>
                                <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                                    <Form.Item className="searchForm" name="phone" label="휴대전화 번호">
                                        <Input disabled placeholder={user?.phone.replace(/^(\d{2,3})(\d{3,4})(\d{4})$/, `$1-$2-$3`) || String.dash} />
                                    </Form.Item>
                                </Col>
                                <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                                    <Form.Item className="searchForm" name="password" label="비밀번호">
                                        <Button onClick={() => setResetPassword(true)}>비밀번호 초기화</Button>
                                    </Form.Item>
                                </Col>
                                <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                                    <Form.Item className="searchForm" name="companyName" label="회사명">
                                        <Input disabled={!checkCorporate ? true : !inputType} placeholder={form.getFieldValue("companyName") || String.dash} />
                                    </Form.Item>
                                </Col>
                                <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                                    <Form.Item className="searchForm" name="companyRegistrationNo" label="사업자 등록번호">
                                        <Input
                                            disabled={!checkCorporate ? true : !inputType}
                                            placeholder={form.getFieldValue("companyRegistrationNo") || String.dash}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                                    <Form.Item className="searchForm" name="address" label="주소">
                                        <Input
                                            disabled={!inputType}
                                            placeholder={
                                                form.getFieldValue("address") && form.getFieldValue("addressDetails")
                                                    ? form.getFieldValue("address") + "," + form.getFieldValue("addressDetails")
                                                    : form.getFieldValue("address")
                                                    ? form.getFieldValue("address")
                                                    : String.dash
                                            }
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                                    <Form.Item className="searchForm" name="promotionCodes" label="회사/단체">
                                        <Select
                                            disabled={!inputType}
                                            allowClear
                                            mode="multiple"
                                            options={b2bs?.map((b2b: Types.B2bDto) => ({ label: b2b.b2bName, value: b2b.promotionCode }))}
                                        />
                                    </Form.Item>
                                </Col>
                            </Col>
                            <Col span={12} style={{ paddingRight: "0" }}>
                                <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                                    <Form.Item className="searchForm" label="계정상태">
                                        <Radio.Group
                                            disabled={!inputType}
                                            onChange={(e) => {
                                                setOpenCheckbox(e.target.value);
                                                !e.target.value &&
                                                    setAccountStatus({
                                                        blocked: false,
                                                        accountLocked: false,
                                                        accountExpired: false,
                                                        withdrawn: false,
                                                    });
                                            }}
                                            value={openCheckbox}
                                        >
                                            <Space direction="vertical">
                                                <Radio value={false}>정상</Radio>
                                                <Radio value={true}>
                                                    비정상
                                                    <Checkbox.Group
                                                        style={{ marginLeft: "15px" }}
                                                        disabled={!openCheckbox || !inputType}
                                                        onChange={(value) => onChange(value)}
                                                        value={
                                                            accountStatus &&
                                                            Object.keys(accountStatus).filter((key) => accountStatus[key as keyof AccountStatusType])
                                                        }
                                                    >
                                                        <Checkbox value="blocked">블랙</Checkbox>
                                                        <Checkbox value="accountLocked">잠김</Checkbox>
                                                        <Checkbox value="accountExpired">휴면</Checkbox>
                                                        <Checkbox value="withdrawn">탈퇴</Checkbox>
                                                    </Checkbox.Group>
                                                </Radio>
                                            </Space>
                                        </Radio.Group>
                                    </Form.Item>
                                </Col>
                                <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                                    <Form.Item className="searchForm" name="authority" label="관리자 권한">
                                        <Radio.Group disabled={!inputType}>
                                            <Space direction="vertical">
                                                <Radio value="ROLE_USER">없음</Radio>
                                                <div>
                                                    <Radio value="ROLE_ADMIN">최고운영자</Radio>
                                                    <Radio value="ROLE_MANAGER">통합관리자</Radio>
                                                    <Radio value="ROLE_RENT_MANAGER">렌트관리자</Radio>
                                                    <Radio value="ROLE_B2B_MANAGER">B2B관리자</Radio>
                                                </div>
                                            </Space>
                                        </Radio.Group>
                                    </Form.Item>
                                </Col>
                                <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                                    <Form.Item className="searchForm" name="createdDate" label="가입일">
                                        <Input disabled placeholder={user?.createdDate || String.dash} />
                                    </Form.Item>
                                </Col>
                                <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                                    <Form.Item className="searchForm" name="withdrawnDate" label="탈퇴일">
                                        <Input disabled placeholder={user?.blockedDate || String.dash} />
                                    </Form.Item>
                                </Col>
                                <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                                    <Form.Item className="searchForm" name="lastLoginDate" label="마지막 로그인">
                                        <Input disabled placeholder={user?.lastLoginDate || String.dash} />
                                    </Form.Item>
                                </Col>
                                <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                                    <Form.Item className="searchForm" name="blockedDate" label="블랙 설정일">
                                        <Input disabled placeholder={user?.blockedDate || String.dash} />
                                    </Form.Item>
                                </Col>
                                <Col xs={{ span: 24 }} sm={{ span: 12 }} className="d-none">
                                    <Form.Item className="searchForm" name="memo" label="기타">
                                        <Input.TextArea
                                            disabled={!inputType}
                                            placeholder="고객 관련 메모 입력란입니다.&#13;&#10;(블랙,휴면,탈퇴 처리를 임의로 진행할 경우 사유를 입력해주세요.)"
                                            style={{ color: "black" }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Col>
                        </Row>
                    </Form>
                </div>

                {/* 면허 정보 */}
                <DriverLicenseInfo driverLicenseId={user?.driverLicense?.driverLicenseId} />

                {/* 쿠폰정보 */}
                <MyCoupon user={user} />

                {/* 이용 내역 */}
                <MyHistory user={user} />

                {/* 비밀번호 초기화 팝업 */}
                <Modal
                    width={540}
                    centered
                    open={resetPassword}
                    title="비밀번호 초기화"
                    footer={[
                        <Button onClick={() => setResetPassword(false)}>취소</Button>,
                        <Button
                            onClick={() => {
                                form.setFieldValue("password", newPassword);
                                setResetPassword(false);
                            }}
                        >
                            발행
                        </Button>,
                    ]}
                    closable={false}
                >
                    <Space direction="vertical" style={{ textAlign: "center", width: "100%" }}>
                        <p style={{ margin: 0 }}>비밀번호를 초기화 하시겠습니까?</p>
                        <p style={{ margin: 0 }}>초기화 비밀번호 : {newPassword}</p>
                    </Space>
                </Modal>

                {/* 강제 삭제 팝업 */}
                <Modal
                    width={540}
                    centered
                    open={confirmModalShow}
                    title="회원 정보 강제 삭제"
                    footer={[
                        <Button onClick={() => setConfirmModalShow(false)}>취소</Button>,
                        <Button
                            danger
                            onClick={() => {
                                // 임의로 console 만 찍는중
                                console.log("탈퇴");
                                setConfirmModalShow(false);
                            }}
                        >
                            탈퇴
                        </Button>,
                    ]}
                    closable={false}
                    style={{ marginTop: "80px" }}
                >
                    <Space direction="vertical" size={10}>
                        <p style={{ margin: 0 }}>
                            선택한 회원 정보를 강제 삭제 처리합니다. 해당 회원의 구매이력 등 주요 정보들도 삭제되므로 최종 확인 후 신중히 진행해 주세요.
                        </p>
                        <Row>
                            <Col span={24}>
                                <p style={{ margin: "16px 0 5px 0" }}>계정 타입</p>
                                <Radio.Group
                                    disabled
                                    options={[
                                        { label: "개인 회원", value: "INDIVIDUAL" },
                                        { label: "법인 회원", value: "CORPORATE" },
                                    ]}
                                    value={user?.userType}
                                />
                            </Col>
                            <Col span={24}>
                                <p style={{ margin: "16px 0 5px 0" }}>이름</p>
                                <Input disabled value={user?.name || String.dash} />
                            </Col>
                            <Col span={24}>
                                <p style={{ margin: "16px 0 5px 0" }}>이메일</p>
                                <Input disabled value={user?.email || String.dash} />
                            </Col>
                            <Col span={24}>
                                <p style={{ margin: "16px 0 5px 0" }}>휴대전화 번호</p>
                                <Input disabled value={user?.phone.replace(/^(\d{2,3})(\d{3,4})(\d{4})$/, `$1-$2-$3`) || String.dash} />
                            </Col>
                            {user?.userType === "CORPORATE" && (
                                <Col span={24}>
                                    <p style={{ margin: "16px 0 5px 0" }}>회사명</p>
                                    <Input disabled value={user?.companyName || String.dash} />
                                    <p style={{ margin: "16px 0 5px 0" }}>사업자 등록번호</p>
                                    <Input disabled value={user?.companyRegistrationNo || String.dash} />
                                </Col>
                            )}
                            <Col span={24}>
                                <p style={{ margin: "16px 0 5px 0" }}>주소</p>
                                <Input
                                    disabled
                                    value={
                                        user?.address && user?.addressDetails
                                            ? user.address + "," + user.addressDetails
                                            : user?.address
                                            ? user.address
                                            : String.dash
                                    }
                                />
                            </Col>
                            <Col span={24}>
                                <p style={{ margin: "16px 0 5px 0" }}>면허종류</p>
                                <Input disabled value={user?.driverLicense?.licenseType || String.dash} />
                            </Col>
                            <Col span={24}>
                                <p style={{ margin: "16px 0 5px 0" }}>계정상태</p>
                                <Input
                                    disabled
                                    value={
                                        // todo: 중복 가능
                                        form.getFieldValue("accountExpired")
                                            ? "만료"
                                            : form.getFieldValue("accountLocked")
                                            ? "잠김"
                                            : form.getFieldValue("blocked")
                                            ? "블랙"
                                            : "정상"
                                    }
                                />
                            </Col>
                        </Row>
                    </Space>
                </Modal>
            </Space>
        </div>
    );
}

export default UserDetail;
